import { createSignal } from 'solid-js';
import { PolygonMeasurement } from '@technology/web3d-plugin-markup';
import {
  DEFAULT_POLYGON_MEASUREMENT_SETTINGS,
  type LineStyle,
  type MeasurementsCapabilityProvider,
  type AreaMeasurementType,
} from '@trimble/web3d-providers';
import { createBaseMeasurementController } from '@trimble/measurements-shared';
import type { AreaMeasurementController } from './area-measurement.types';

export function createAreaMeasurementController(
  provider: MeasurementsCapabilityProvider
): AreaMeasurementController {
  const [fillOpacity, setFillOpacitySignal] = createSignal<number>(
    DEFAULT_POLYGON_MEASUREMENT_SETTINGS.fillOpacity
  );
  const [lineThickness, setLineThicknessSignal] = createSignal<number>(
    DEFAULT_POLYGON_MEASUREMENT_SETTINGS.lineThickness
  );
  const [lineStyle, setLineStyleSignal] = createSignal<LineStyle>(
    DEFAULT_POLYGON_MEASUREMENT_SETTINGS.lineStyle
  );

  const base = createBaseMeasurementController<AreaMeasurementType>(provider, {
    applyMarkupProperties: (markup) => {
      (markup as PolygonMeasurement).lineStyle = lineStyle();
      (markup as PolygonMeasurement).lineThickness = lineThickness();
      (markup as PolygonMeasurement).fillOpacity = fillOpacity();
    },
    getExtraUpdateFields: () => ({
      fillOpacity: fillOpacity(),
      lineThickness: lineThickness(),
      lineStyle: lineStyle(),
    }),
    onInitialize: (measurement) => {
      setFillOpacitySignal(
        measurement.fillOpacity ??
          DEFAULT_POLYGON_MEASUREMENT_SETTINGS.fillOpacity
      );
      setLineThicknessSignal(
        measurement.lineThickness ??
          DEFAULT_POLYGON_MEASUREMENT_SETTINGS.lineThickness
      );
      setLineStyleSignal(
        measurement.lineStyle ?? DEFAULT_POLYGON_MEASUREMENT_SETTINGS.lineStyle
      );
    },
  });

  const setFillOpacity = (value: number): void => {
    setFillOpacitySignal(value);
    base.update();
  };

  const setLineThickness = (value: number): void => {
    setLineThicknessSignal(value);
    base.update();
  };

  const setLineStyle = (value: LineStyle): void => {
    setLineStyleSignal(value);
    base.update();
  };

  return {
    type: 'area',
    ...base,
    fillOpacity,
    lineThickness,
    lineStyle,
    setFillOpacity,
    setLineThickness,
    setLineStyle,
  };
}
